import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Receipt, ChevronRight, Ban } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/common/Card'
import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { EmptyState, SkeletonRow } from '@/components/common/Loader'
import { billingApi } from '@/api/billingApi'
import { cn, formatDate, formatRupees } from '@/lib/utils'

function BillStatusBadge({ status, balance }) {
  if (status === 'cancelled') return <Badge tone="muted" leftIcon={<Ban className="h-3 w-3" />}>Cancelled</Badge>
  if (Number(balance) <= 0) return <Badge tone="success">Paid</Badge>
  return <Badge tone="accent">Balance due</Badge>
}

export function PatientBillsSection({ patient }) {
  const [bills, setBills] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!patient?.patient_id) return
    let alive = true
    setLoading(true)
    billingApi
      .listForPatient(patient.patient_id, { silent: true })
      .then((rows) => {
        if (alive) setBills(rows || [])
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [patient?.patient_id])

  const summary = bills.reduce(
    (acc, b) => {
      if (b.status === 'cancelled') return acc
      acc.total += Number(b.total || 0)
      acc.paid += Number(b.paid || 0)
      acc.balance += Number(b.balance || 0)
      return acc
    },
    { total: 0, paid: 0, balance: 0 },
  )

  return (
    <Card>
      <CardHeader
        title="Bills"
        eyebrow="Phase 7 — Billing"
        description="Every bill raised for this patient with payment status."
        action={
          <Link
            to={`/billing/new?patient_id=${patient.patient_id}`}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-accent-500 px-3.5 text-sm font-semibold text-cream shadow-soft transition hover:bg-accent-600"
          >
            <Plus className="h-4 w-4" />
            New bill
          </Link>
        }
      />
      <CardBody className="!pt-0">
        {loading ? (
          <div className="space-y-2">
            <SkeletonRow className="h-14" />
            <SkeletonRow className="h-14" />
          </div>
        ) : bills.length === 0 ? (
          <EmptyState
            icon={<Receipt className="h-6 w-6" />}
            title="No bills yet"
            description="When you raise a bill it'll appear here with the dispensed medicines."
            action={
              <Link to={`/billing/new?patient_id=${patient.patient_id}`}>
                <Button leftIcon={<Plus className="h-4 w-4" />}>Create first bill</Button>
              </Link>
            }
          />
        ) : (
          <div className="space-y-3">
            {/* Mini summary */}
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-xl bg-cream/60 p-3 ring-1 ring-ink-100">
                <p className="label-text">Lifetime total</p>
                <p className="mt-1 font-bold tabular-nums text-ink-900">
                  {formatRupees(summary.total)}
                </p>
              </div>
              <div className="rounded-xl bg-cream/60 p-3 ring-1 ring-ink-100">
                <p className="label-text">Collected</p>
                <p className="mt-1 font-bold tabular-nums text-primary-800">
                  {formatRupees(summary.paid)}
                </p>
              </div>
              <div className="rounded-xl bg-cream/60 p-3 ring-1 ring-ink-100">
                <p className="label-text">Outstanding</p>
                <p
                  className={cn(
                    'mt-1 font-bold tabular-nums',
                    summary.balance > 0 ? 'text-accent-700' : 'text-ink-900',
                  )}
                >
                  {formatRupees(summary.balance)}
                </p>
              </div>
            </div>

            <ul className="divide-y divide-ink-100 overflow-hidden rounded-xl bg-white ring-1 ring-ink-100">
              {bills.map((b) => (
                <li key={b.bill_id}>
                  <Link
                    to={`/billing/${b.bill_id}`}
                    className="flex items-center gap-3 px-3 py-3 transition hover:bg-cream/60"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                      <Receipt className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink-900">
                        {b.bill_id}
                      </p>
                      <p className="text-[11px] text-ink-500">
                        {formatDate(b.bill_date)} · {b.items?.length || 0} medicine
                        {(b.items?.length || 0) === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="hidden sm:block text-right">
                      <p className="text-sm font-semibold tabular-nums text-ink-900">
                        {formatRupees(b.total)}
                      </p>
                      <p
                        className={cn(
                          'text-[11px] tabular-nums',
                          Number(b.balance) > 0 ? 'text-accent-700' : 'text-emerald-700',
                        )}
                      >
                        Bal {formatRupees(b.balance)}
                      </p>
                    </div>
                    <BillStatusBadge status={b.status} balance={b.balance} />
                    <ChevronRight className="h-4 w-4 text-ink-400" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
